import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Buildings, Plus, Pencil, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BranchesPage = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/branches`);
      setBranches(data);
    } catch (e) {
      toast.error('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await axios.put(`${API_URL}/api/branches/${editingBranch.id}`, formData);
        toast.success('Branch updated successfully');
      } else {
        await axios.post(`${API_URL}/api/branches`, formData);
        toast.success('Branch created successfully');
      }
      fetchBranches();
      closeDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save branch');
    }
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('Are you sure you want to deactivate this branch?')) return;
    try {
      await axios.delete(`${API_URL}/api/branches/${branchId}`);
      toast.success('Branch deactivated');
      fetchBranches();
    } catch (e) {
      toast.error('Failed to deactivate branch');
    }
  };

  const handleHardDelete = async (branchId) => {
    if (!window.confirm('PERMANENTLY delete this branch? All branch data associations will be lost. This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/branches/${branchId}?hard=true`);
      toast.success('Branch permanently deleted');
      fetchBranches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete branch');
    }
  };

  const openEditDialog = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      code: branch.code,
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || ''
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingBranch(null);
    setFormData({ name: '', code: '', address: '', phone: '', email: '' });
  };

  const filteredBranches = branches.filter(b => 
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 skeleton"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-48 skeleton rounded-md"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Branches</h1>
          <p className="text-muted-foreground">Manage your branch locations</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingBranch(null); setFormData({ name: '', code: '', address: '', phone: '', email: '' }); }} data-testid="add-branch-btn">
              <Plus size={18} className="mr-2" />
              Add Branch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBranch ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
              <DialogDescription>
                {editingBranch ? 'Update branch details' : 'Create a new branch location'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Branch Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Main Branch"
                    required
                    data-testid="branch-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Branch Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="BR001"
                    required
                    data-testid="branch-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main St, City"
                  data-testid="branch-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    data-testid="branch-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="branch@example.com"
                    data-testid="branch-email-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button type="submit" data-testid="save-branch-btn">
                  {editingBranch ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search branches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-branches"
        />
      </div>

      {filteredBranches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBranches.map(branch => (
            <Card key={branch.id} className="card-hover">
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <Buildings size={20} className="text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{branch.name}</CardTitle>
                    <Badge variant="outline" className="mt-1">{branch.code}</Badge>
                  </div>
                </div>
                <Badge variant={branch.is_active ? 'default' : 'secondary'}>
                  {branch.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {branch.address && <p>{branch.address}</p>}
                  {branch.phone && <p>{branch.phone}</p>}
                  {branch.email && <p>{branch.email}</p>}
                </div>
                {user?.role === 'super_admin' && (
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(branch)} data-testid={`edit-branch-${branch.id}`}>
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    {branch.is_active ? (
                      <Button variant="outline" size="sm" onClick={() => handleDelete(branch.id)} className="text-amber-600" data-testid={`deactivate-branch-${branch.id}`}>
                        <Trash size={14} className="mr-1" /> Deactivate
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => handleHardDelete(branch.id)} className="text-destructive" data-testid={`hard-delete-branch-${branch.id}`}>
                        <Trash size={14} className="mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="empty-state py-12">
            <Buildings size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No branches found</p>
            <p className="text-sm">Create your first branch to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BranchesPage;
